import { Box, Chip, Divider, Tooltip, Typography } from '@mui/material'
import { CheckCircleOutline, HelpOutline, RemoveCircleOutline } from '@mui/icons-material'

/**
 * How this agent behaves when something goes wrong.
 *
 * A program fails by raising. An agent fails by *continuing* — confidently,
 * on a bad result — so the question worth answering is not "is there a
 * try/except" but "what happens when the agent cannot do what it was asked".
 * That is an agentic property, and the five below are the ones that decide it.
 *
 * Every one of them is derived from signals the scanner already reports, and
 * this component reads them off the agent record rather than computing
 * anything new. It deliberately replaces `abf.error_posture_score`, which
 * hunted the file text for Python keywords: that answered a different
 * question, scored spec-defined agents 0 because they have no file text to
 * search, and rendered "0/100 (Weak)" for agents nothing had ever assessed.
 *
 * Unknown is a first-class answer here. Where the scanner reports nothing
 * either way we say so, rather than counting silence as a weakness — an
 * absence read as a verdict is the failure this whole component exists to
 * correct.
 */

export type Verdict = 'present' | 'absent' | 'unknown'

export interface ResilienceDimension {
  key: string
  label: string
  verdict: Verdict
  /** What this protects against, in the language of agent behaviour. */
  why: string
  /** What we actually saw, so the verdict is never unexplained. */
  basis: string
}

const has = (list: unknown, ...names: string[]) =>
  Array.isArray(list) && names.some((n) => list.includes(n))

/** Issue text the scanner emits, used as corroboration rather than guesswork. */
const issuesOf = (agent: any): string[] => {
  const out: string[] = []
  for (const holder of ['tool_quality', 'guardrail_effectiveness', 'external_integrations', 'protocol_governance']) {
    const v = agent?.[holder]
    if (v && typeof v === 'object') {
      for (const item of [...(v.issues || []), ...(v.findings || [])]) {
        const text = typeof item === 'string' ? item : item?.issue
        if (text) out.push(String(text))
      }
    }
  }
  return out
}

export const deriveResilience = (agent: any): ResilienceDimension[] => {
  const guardrails = agent?.guardrails
  const knowsGuardrails = Array.isArray(guardrails)
  const issues = issuesOf(agent)
  const said = (fragment: string) => issues.some((i) => i.toLowerCase().includes(fragment))

  const isAutonomous = agent?.autonomy === 'autonomous'
  const hasTools = Array.isArray(agent?.tools) ? agent.tools.length > 0 : undefined

  const dims: ResilienceDimension[] = []

  // 1. Does the agent check the model's output before acting on it?
  dims.push({
    key: 'output_validation',
    label: 'Validates model output',
    verdict: !knowsGuardrails ? 'unknown'
      : has(guardrails, 'output_parser', 'output_validation') ? 'present' : 'absent',
    why: 'An agent that acts on unvalidated model output will act confidently on a wrong one.',
    basis: !knowsGuardrails
      ? 'the scan reported no guardrail information for this agent'
      : has(guardrails, 'output_parser', 'output_validation')
        ? 'an output parser or validator was detected'
        : said('no output validation')
          ? 'the scan flagged tool results as unverified'
          : 'no output parser or validator was detected',
  })

  // 2. Can it stop? An agent that cannot converge and cannot stop burns cost.
  dims.push({
    key: 'bounded_execution',
    label: 'Bounded execution',
    verdict: !knowsGuardrails ? 'unknown'
      : has(guardrails, 'max_iterations', 'timeout') ? 'present'
        : agent?.constructed_in_unbounded_loop === true ? 'absent'
          : isAutonomous ? 'absent' : 'unknown',
    why: 'Without a limit, an agent that cannot finish keeps trying — indefinitely, and at cost.',
    basis: has(guardrails, 'max_iterations', 'timeout')
      ? 'an iteration limit or timeout was detected'
      : said('iteration limits')
        ? 'the scan flagged this agent as able to run indefinitely'
        : agent?.constructed_in_unbounded_loop === true
          ? 'the agent is constructed inside an unbounded loop'
          : 'no iteration limit or timeout was detected',
  })

  // 3. When a tool or the model fails transiently, does anything absorb it?
  dims.push({
    key: 'tool_resilience',
    label: 'Handles tool failure',
    verdict: has(guardrails, 'rate_limit') ? 'present'
      : said('no retry strategy') || said('without error handling') ? 'absent'
        : 'unknown',
    why: 'A tool call that fails silently leaves the agent reasoning from a gap it cannot see.',
    basis: has(guardrails, 'rate_limit')
      ? 'a retry or rate-limit policy was detected'
      : said('no retry strategy')
        ? 'the scan flagged no retry strategy for model calls'
        : said('without error handling')
          ? 'the scan flagged external calls without error handling'
          : 'the scan reported nothing either way',
  })

  // 4. When it genuinely cannot finish, is there a person?
  dims.push({
    key: 'escalation',
    label: 'Escalates to a human',
    verdict: agent?.autonomy === 'human_in_loop' ? 'present'
      : has(guardrails, 'human_in_the_loop', 'approval', 'human_approval') ? 'present'
        : isAutonomous && hasTools ? 'absent'
          : agent?.autonomy ? 'unknown' : 'unknown',
    why: 'An agent with no way to hand back will produce something rather than nothing.',
    basis: agent?.autonomy === 'human_in_loop'
      ? 'the agent runs with a human in the loop'
      : has(guardrails, 'human_in_the_loop', 'approval', 'human_approval')
        ? 'an approval or human-in-the-loop guardrail was detected'
        : said('no human approval gate')
          ? 'the scan flagged an autonomous agent with tools and no approval gate'
          : 'no approval or hand-back path was detected',
  })

  // 5. If it fails, does anyone find out?
  dims.push({
    key: 'visibility',
    label: 'Failures are visible',
    verdict: said('without logging') || said('not auditable') || said('no request correlation') ? 'absent'
      : 'unknown',
    why: 'A silent failure is worse than a loud one: the output still looks like an answer.',
    basis: said('without logging') || said('not auditable')
      ? 'the scan flagged tool or service calls without logging'
      : said('no request correlation')
        ? 'the scan flagged no request correlation across operations'
        : 'the scan reported nothing either way',
  })

  return dims
}

const ICONS: Record<Verdict, JSX.Element> = {
  present: <CheckCircleOutline sx={{ fontSize: 14, color: '#4caf50' }} />,
  absent: <RemoveCircleOutline sx={{ fontSize: 14, color: '#f44336' }} />,
  unknown: <HelpOutline sx={{ fontSize: 14, color: '#9e9e9e' }} />,
}

/** A short label for the chip. Never a score: a number invites false precision. */
export const resilienceSummary = (dims: ResilienceDimension[]) => {
  const absent = dims.filter((d) => d.verdict === 'absent').length
  const present = dims.filter((d) => d.verdict === 'present').length
  const unknown = dims.filter((d) => d.verdict === 'unknown').length
  if (unknown === dims.length) return { label: 'Not assessed', color: '#9e9e9e' }
  if (absent === 0) return { label: `${present} protection${present === 1 ? '' : 's'}`, color: '#4caf50' }
  if (absent >= 3) return { label: `${absent} gaps`, color: '#f44336' }
  return { label: `${absent} gap${absent === 1 ? '' : 's'}`, color: '#ff9800' }
}

export const ResilienceBreakdown = ({ dims }: { dims: ResilienceDimension[] }) => (
  <Box sx={{ maxWidth: 340 }}>
    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
      What happens when this agent fails
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', mb: 1, opacity: 0.85 }}>
      An agent rarely crashes — it continues, on a bad result. These are the
      protections that decide what it does instead.
    </Typography>
    <Divider sx={{ mb: 1 }} />
    {dims.map((d) => (
      <Box key={d.key} sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {ICONS[d.verdict]}
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {d.label}
            {d.verdict === 'unknown' && ' — not assessed'}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ display: 'block', pl: 2.5, opacity: 0.85 }}>
          {d.basis}
        </Typography>
      </Box>
    ))}
  </Box>
)

/** The chip that replaces "Error handling: Weak". */
const AgentResilience = ({ agent }: { agent: any }) => {
  const dims = deriveResilience(agent)
  const summary = resilienceSummary(dims)
  return (
    <Tooltip title={<ResilienceBreakdown dims={dims} />} arrow>
      <Chip
        label={`Failure handling: ${summary.label}`}
        size="small"
        variant="outlined"
        sx={{ fontSize: '0.65rem', height: 20, borderColor: summary.color, color: summary.color, cursor: 'help' }}
      />
    </Tooltip>
  )
}

export default AgentResilience
